"use client";

/**
 * EmailCampaignDisplay - Email campaign creation and status
 *
 * Displays campaign results with:
 * - Status banner with campaign state
 * - Stat cards for recipients/sent/delivered/opened
 * - Progress bar for in-flight campaigns
 * - Recipient list with per-recipient status
 */

import React, { useState } from "react";
import { Tag, Alert } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import {
  Campaign,
  Schedule,
  Error as ErrorIcon,
  Send,
  Drafts,
  MarkEmailRead,
  Email,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
  cleanErrorMessage,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";
import { SendConfirmationCard, isPendingSend } from "./SendConfirmationCard";

interface Recipient {
  id?: string | number;
  name?: string;
  email: string;
  status?: "pending" | "sent" | "delivered" | "opened" | "failed";
}

interface CampaignResult {
  success?: boolean;
  campaignId?: string;
  campaignName?: string;
  status?: "draft" | "scheduled" | "sending" | "sent" | "completed" | "success" | "error";
  scheduledAt?: string;
  sentAt?: string;
  recipients?: Recipient[];
  totalRecipients?: number;
  sentCount?: number;
  deliveredCount?: number;
  openedCount?: number;
  failedCount?: number;
  message?: string;
  error?: string;
}

const STATUS_CONFIG: Record<string, { color: "neutral" | "info" | "primary" | "success"; label: string }> = {
  draft: { color: "neutral", label: "Draft" },
  scheduled: { color: "info", label: "Scheduled" },
  sending: { color: "primary", label: "Sending" },
  sent: { color: "success", label: "Sent" },
  completed: { color: "success", label: "Completed" },
  success: { color: "success", label: "Success" },
  error: { color: "neutral", label: "Error" },
};

const RECIPIENT_STATUS: Record<string, { icon: React.ReactNode; color: string }> = {
  pending: { icon: <Schedule style={{ fontSize: 16 }} />, color: colors.text.muted },
  sent: { icon: <Send style={{ fontSize: 16 }} />, color: colors.action.product },
  delivered: { icon: <Drafts style={{ fontSize: 16 }} />, color: colors.signal.info },
  opened: { icon: <MarkEmailRead style={{ fontSize: 16 }} />, color: colors.signal.success },
  failed: { icon: <ErrorIcon style={{ fontSize: 16 }} />, color: colors.signal.danger },
};

function StatCard({ value, label, color }: { value: number; label: string; color?: string }) {
  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: 80,
        padding: `${spacing[3]}px ${spacing[4]}px`,
        backgroundColor: primitives.neutral[50],
        borderRadius: radius.md,
        textAlign: "center",
      }}
    >
      <div
        style={{
          ...textStyles.h3,
          fontWeight: 700,
          color: color || colors.text.primary,
        }}
      >
        {value.toLocaleString()}
      </div>
      <div style={{ ...textStyles.caption, color: colors.text.muted, marginTop: spacing[1] }}>
        {label}
      </div>
    </div>
  );
}

function RecipientRow({ recipient }: { recipient: Recipient }) {
  const statusInfo = RECIPIENT_STATUS[recipient.status || "pending"] || RECIPIENT_STATUS.pending;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing[3],
        padding: `${spacing[2]}px 0`,
        borderBottom: `1px solid ${colors.border.subtle}`,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: colors.action.productLight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: statusInfo.color,
        }}
      >
        {statusInfo.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...textStyles.body2, fontWeight: 500, display: "block" }}>
          {recipient.name || recipient.email}
        </span>
        {recipient.name && (
          <span style={{ ...textStyles.caption, color: colors.text.muted }}>
            {recipient.email}
          </span>
        )}
      </div>
      {recipient.status && (
        <Tag
          size="sm"
          color={
            recipient.status === "opened" ? "success"
            : recipient.status === "failed" ? "danger"
            : recipient.status === "sent" || recipient.status === "delivered" ? "info"
            : "neutral"
          }
        >
          {recipient.status.charAt(0).toUpperCase() + recipient.status.slice(1)}
        </Tag>
      )}
    </div>
  );
}

export function EmailCampaignDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as CampaignResult | undefined;

  if (isPendingSend(toolCall.finalOutput)) {
    return <SendConfirmationCard pending={toolCall.finalOutput} />;
  }

  const isSuccess = result?.success === true || (result?.status !== "error" && result?.success !== false);

  const getProgressPercentage = (): number => {
    if (!result?.totalRecipients) return 0;
    const sent = result.sentCount || 0;
    return Math.round((sent / result.totalRecipients) * 100);
  };

  const hasStats =
    result?.totalRecipients !== undefined ||
    result?.sentCount !== undefined ||
    result?.deliveredCount !== undefined ||
    result?.openedCount !== undefined;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={result?.campaignName || "Email Campaign"}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Campaign style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Processing campaign..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && result && (
          <>
            {/* Status Banner */}
            {result.status && (
              <div
                style={{
                  padding: `${spacing[3]}px ${spacing[5]}px`,
                  backgroundColor: primitives.neutral[50],
                  borderBottom: `1px solid ${colors.border.subtle}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: spacing[3] }}>
                  <Tag
                    size="sm"
                    color={STATUS_CONFIG[result.status]?.color || "neutral"}
                    variant="filled"
                  >
                    {STATUS_CONFIG[result.status]?.label || result.status}
                  </Tag>
                  {/* Show message inline when status is a success state */}
                  {isSuccess && result.message && (
                    <span style={{ ...textStyles.body2, color: colors.text.muted }}>
                      {result.message}
                    </span>
                  )}
                  {result.campaignId && (
                    <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                      ID: {result.campaignId}
                    </span>
                  )}
                </div>
                {result.scheduledAt && (
                  <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                    <Schedule style={{ fontSize: 14, color: colors.text.muted }} />
                    <span style={{ ...textStyles.caption, color: colors.text.muted }}>
                      {new Date(result.scheduledAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Sending Progress */}
            {result.status === "sending" && result.totalRecipients && (
              <div
                style={{
                  padding: `${spacing[3]}px ${spacing[5]}px`,
                  borderBottom: `1px solid ${colors.border.subtle}`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: spacing[2] }}>
                  <span style={{ ...textStyles.body2, color: colors.text.primary }}>
                    Sending Progress
                  </span>
                  <span style={{ ...textStyles.body2, fontWeight: 600 }}>
                    {result.sentCount || 0} / {result.totalRecipients}
                  </span>
                </div>
                <div
                  style={{
                    height: 6,
                    backgroundColor: primitives.neutral[100],
                    borderRadius: radius.pill,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${getProgressPercentage()}%`,
                      backgroundColor: colors.action.product,
                      borderRadius: radius.pill,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Stats */}
            {hasStats && (
              <div
                style={{
                  padding: `${spacing[4]}px ${spacing[5]}px`,
                  borderBottom: `1px solid ${colors.border.subtle}`,
                }}
              >
                <span
                  style={{
                    ...textStyles.label,
                    color: colors.text.muted,
                    display: "block",
                    marginBottom: spacing[3],
                  }}
                >
                  Campaign Stats
                </span>
                <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
                  {result.totalRecipients !== undefined && (
                    <StatCard value={result.totalRecipients} label="Recipients" />
                  )}
                  {result.sentCount !== undefined && (
                    <StatCard value={result.sentCount} label="Sent" color={colors.action.product} />
                  )}
                  {result.deliveredCount !== undefined && (
                    <StatCard value={result.deliveredCount} label="Delivered" color={colors.signal.info} />
                  )}
                  {result.openedCount !== undefined && (
                    <StatCard value={result.openedCount} label="Opened" color={colors.signal.success} />
                  )}
                  {result.failedCount !== undefined && result.failedCount > 0 && (
                    <StatCard value={result.failedCount} label="Failed" color={colors.signal.danger} />
                  )}
                </div>
              </div>
            )}

            {/* Recipients Preview */}
            {result.recipients && result.recipients.length > 0 && (
              <div style={{ padding: `${spacing[3]}px ${spacing[5]}px` }}>
                <span
                  style={{
                    ...textStyles.label,
                    color: colors.text.muted,
                    display: "block",
                    marginBottom: spacing[2],
                  }}
                >
                  Recipients
                </span>
                <div>
                  {result.recipients.slice(0, 5).map((recipient, i) => (
                    <RecipientRow key={recipient.id || recipient.email || i} recipient={recipient} />
                  ))}
                  {result.recipients.length > 5 && (
                    <span
                      style={{
                        ...textStyles.caption,
                        color: colors.text.muted,
                        display: "block",
                        paddingTop: spacing[2],
                        textAlign: "center",
                      }}
                    >
                      +{result.recipients.length - 5} more recipients
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Error Message (success messages shown inline in status banner) */}
            {result.message && !isSuccess && (
              <div style={{ padding: spacing[4], borderTop: `1px solid ${colors.border.subtle}` }}>
                <Alert variant="error" title="Error">
                  {cleanErrorMessage(result.message)}
                </Alert>
              </div>
            )}

            {/* Success message without status banner (fallback) */}
            {result.message && isSuccess && !result.status && (
              <div style={{ padding: spacing[4], borderTop: `1px solid ${colors.border.subtle}` }}>
                <Alert variant="success" title="Success">
                  {result.message}
                </Alert>
              </div>
            )}

            {result.error && !result.message && (
              <div style={{ padding: spacing[4], borderTop: `1px solid ${colors.border.subtle}` }}>
                <Alert variant="error" title="Error">
                  {cleanErrorMessage(result.error)}
                </Alert>
              </div>
            )}
          </>
        )}

        {!isLoading && !result && !isError && (
          <ToolEmptyState icon={<Email />} message="No campaign data available" />
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default EmailCampaignDisplay;
